/**
 * Individual field validators
 *
 * Every `reason` is a ValidationException::CODE_* value, so one GA dimension
 * reads the same whichever side rejected the value.
 */

import { isRealDate, isValidAge, resolveMinAge } from '../utils/dateHelpers.js';
import { t } from '../../i18n.js';

export function validateName(name) {
    if (!name || !name.trim()) {
        return { valid: false, reason: 'required', error: t('auth.error_name_min_length') };
    }
    if (name.trim().length < 2) {
        return { valid: false, reason: 'too_short', error: t('auth.error_name_min_length') };
    }
    return { valid: true };
}

export function validateEmail(email) {
    if (!email || !String(email).trim()) {
        return { valid: false, reason: 'required', error: t('auth.error_email_invalid') };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        return { valid: false, reason: 'invalid_email', error: t('auth.error_email_invalid') };
    }
    return { valid: true };
}

/**
 * dayDropped: the step cleared the picked day because the month or the year
 * no longer has it. The error then names the day, not an incomplete date.
 */
export function validateBirthday(day, month, year, minAge, dayDropped = false) {
    // Check for empty, null, undefined, or whitespace-only values
    const hasDay = day && String(day).trim() !== '';
    const hasMonth = month && String(month).trim() !== '';
    const hasYear = year && String(year).trim() !== '';

    if (!hasDay && dayDropped) {
        return { valid: false, reason: 'invalid_value', error: t('auth.error_birthday_invalid_day') };
    }
    if (!hasDay || !hasMonth || !hasYear) {
        return { valid: false, reason: 'required', error: t('auth.error_birthday_incomplete') };
    }
    if (!isRealDate(day, month, year)) {
        return { valid: false, reason: 'invalid_value', error: t('auth.error_birthday_invalid_day') };
    }
    if (!isValidAge(day, month, year, minAge)) {
        return {
            valid: false,
            reason: 'too_young',
            error: t('auth.error_age_requirement', { age: resolveMinAge(minAge) }),
        };
    }
    return { valid: true };
}

export function validateSex(sex) {
    if (!sex || (sex !== 'male' && sex !== 'female')) {
        return { valid: false, reason: 'required', error: t('auth.error_sex_required') };
    }
    return { valid: true };
}

export function validateCity(cityId) {
    if (!cityId || cityId === '' || cityId === '0') {
        return { valid: false, reason: 'required', error: t('auth.error_city_required') };
    }
    return { valid: true };
}

export function validateHomeCity(homeCityId) {
    if (!homeCityId || homeCityId === '' || homeCityId === '0') {
        return { valid: false, reason: 'required', error: t('auth.error_home_city_required') };
    }
    return { valid: true };
}

/**
 * Mirrors PasswordPolicy, used only when the server values never reached the
 * wizard. The submission is judged server-side either way, so this keeps the
 * step from passing something the submission rejects. It is not the policy.
 */
export const DEFAULT_PASSWORD_MIN_LENGTH = 8;
export const DEFAULT_PASSWORD_MAX_LENGTH = 64;
export const DEFAULT_PASSWORD_MAX_BYTES = 72;

/**
 * The length the password rule measures. Trimmed to match the server, which
 * trims before validating and stores the trimmed value. Code points, not
 * UTF-16 units: the server measures with mb_strlen, so `.length` would refuse
 * a password the submission accepts.
 */
export function passwordLength(password) {
    return [...(password || '').trim()].length;
}

export function validatePassword(
    password,
    minLength = DEFAULT_PASSWORD_MIN_LENGTH,
    maxLength = DEFAULT_PASSWORD_MAX_LENGTH,
    maxBytes = DEFAULT_PASSWORD_MAX_BYTES
) {
    const candidate = (password || '').trim();

    if (!candidate) {
        return { valid: false, reason: 'required', error: t('auth.error_password_min_length', { min: minLength }) };
    }

    const length = passwordLength(candidate);

    if (length < minLength) {
        return { valid: false, reason: 'too_short', error: t('auth.error_password_min_length', { min: minLength }) };
    }
    if (length > maxLength) {
        return { valid: false, reason: 'too_long', error: t('auth.error_password_max_length', { max: maxLength }) };
    }
    // bcrypt stops at 72 bytes, so a password well inside maxLength can still
    // exceed the real ceiling.
    if (new TextEncoder().encode(candidate).length > maxBytes) {
        return { valid: false, reason: 'too_long', error: t('validation.password_max_bytes') };
    }
    return { valid: true };
}

export function validateUsername(username) {
    const candidate = (username || '').trim();
    if (!candidate) {
        return { valid: false, reason: 'required', error: t('auth.error_username_min_length') };
    }
    if (candidate.length < 2) {
        return { valid: false, reason: 'too_short', error: t('auth.error_username_min_length') };
    }
    if (candidate.length > 20) {
        return { valid: false, reason: 'too_long', error: t('validation.too_long') };
    }
    // Mirrors UsernameFormat::BODY and UsernamePunctuationRule.
    const usernameRegex = /^[a-zA-Z0-9_](?:[a-zA-Z0-9_.-]*[a-zA-Z0-9_])?$/;
    if (!usernameRegex.test(candidate) || /[_.-]{2}/.test(candidate)) {
        return { valid: false, reason: 'username_invalid_characters', error: t('auth.error_username_format') };
    }
    return { valid: true };
}
